import React, { useEffect, useState } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { useGetProjectsQuery } from "../features/dashboard/apiSlice";
import { useProjectContext } from "./ProjectContext";
import { useCreateProjectMutation } from "../features/projects/apiSlice";

if (
  !process.env.REACT_APP_MAXIMUM_PROJECT_COUNT ||
  isNaN(Number(process.env.REACT_APP_MAXIMUM_PROJECT_COUNT))
) {
  throw new Error(
    "REACT_APP_MAXIMUM_PROJECT_COUNT is not set or is not a number",
  );
}

const MAXIMUM_PROJECT_COUNT = Number(
  process.env.REACT_APP_MAXIMUM_PROJECT_COUNT,
);

interface Props {
  title: string;
  crumbs?: string;
}

const TopBar: React.FC<Props> = ({ title, crumbs }) => {
  const { data: projects, isFetching } = useGetProjectsQuery();
  const { projectId, setProjectId } = useProjectContext();
  const navigate = useNavigate();
  const location = useLocation();
  const [createProject, { isLoading: creating }] = useCreateProjectMutation();

  const role = localStorage.getItem("role");

  const [newProjectModal, setNewProjectModal] = useState(false);
  const [limitReachedModal, setLimitReachedModal] = useState(false);
  const [newProjectForm, setNewProjectForm] = useState({
    name: "",
    project_code: "",
    contract_no: "",
    contractor_name: "",
    description: "",
  });

  useEffect(() => {
    if (projectId == null && projects && projects.length > 0) {
      setProjectId(projects[0].id);
    }
  }, [projectId, projects, setProjectId]);

  const openNewProjectModal = () => {
    if ((projects?.length ?? 0) >= MAXIMUM_PROJECT_COUNT) {
      setLimitReachedModal(true);
      return;
    }
    setNewProjectForm({
      name: "",
      project_code: "",
      contract_no: "",
      contractor_name: "",
      description: "",
    });
    setNewProjectModal(true);
  };

  const handleCreateProject = async () => {
    if (!newProjectForm.name.trim()) return;
    const codeRegex = /^[A-Z0-9]{6}$/;
    if (!codeRegex.test(newProjectForm.project_code.trim())) {
      alert(
        "Project code must be exactly 6 uppercase letters or digits (A–Z, 0–9).",
      );
      return;
    }
    try {
      const project = await createProject({
        name: newProjectForm.name.trim(),
        project_code: newProjectForm.project_code.trim(),
        contract_no: newProjectForm.contract_no.trim() || undefined,
        contractor_name: newProjectForm.contractor_name.trim() || undefined,
        description: newProjectForm.description.trim() || undefined,
      }).unwrap();
      setProjectId(project.id);
      setNewProjectModal(false);
      navigate(`/admin/project-setup${location.search}`);
    } catch (err: any) {
      const msg =
        err?.data?.message ||
        (typeof err?.data === "string" ? err.data : null) ||
        "Failed to create project.";
      // eslint-disable-next-line no-alert
      alert(msg);
    }
  };

  return (
    <>
      <header className="topbar">
        <div>
          {crumbs && <div className="crumbs">{crumbs}</div>}
          <h1>{title}</h1>
        </div>
        <div className="topbar-actions">
          <select
            className="select"
            style={{ minWidth: 240 }}
            value={projectId ?? ""}
            onChange={(e) =>
              setProjectId(e.target.value ? Number(e.target.value) : null)
            }
            disabled={isFetching || !projects?.length}
          >
            {(!projects || projects.length === 0) && (
              <option value="">
                {isFetching ? "Loading projects…" : "No projects"}
              </option>
            )}
            {projects?.map((p) => (
              <option key={p.id} value={p.id}>
                {p.name}
              </option>
            ))}
          </select>
          {role === "admin" && (
            <button
              className="btn btn-sm"
              style={{ whiteSpace: "nowrap" }}
              title="Create new project"
              onClick={openNewProjectModal}
            >
              <i className="bi bi-plus-lg" /> New Project
            </button>
          )}
          <button
            className="btn btn-icon"
            title="Go back to landing page"
            onClick={() => navigate("/select-role")}
          >
            <i className="bi bi-house-door" />
          </button>
        </div>
      </header>

      {/* Create New Project Modal */}
      <div className={`modal-backdrop ${newProjectModal ? "show" : ""}`}>
        <div className="modal">
          <div className="modal-head">
            <h3>Create New Project</h3>
            <button
              className="btn btn-ghost btn-icon"
              onClick={() => setNewProjectModal(false)}
            >
              <i className="bi bi-x-lg" />
            </button>
          </div>
          <div className="modal-body">
            <div className="field">
              <label>
                Name <span style={{ color: "var(--c-danger)" }}>*</span>
              </label>
              <input
                className="input"
                placeholder="e.g. KL Sentral Excavation"
                value={newProjectForm.name}
                onChange={(e) =>
                  setNewProjectForm({ ...newProjectForm, name: e.target.value })
                }
              />
            </div>
            <div className="field">
              <label>
                Project Code <span style={{ color: "var(--c-danger)" }}>*</span>
              </label>
              <input
                className="input"
                placeholder="e.g. AB1234"
                maxLength={6}
                value={newProjectForm.project_code}
                onChange={(e) =>
                  setNewProjectForm({
                    ...newProjectForm,
                    project_code: e.target.value
                      .toUpperCase()
                      .replace(/[^A-Z0-9]/g, ""),
                  })
                }
              />
              <span className="text-muted text-sm">
                6 uppercase letters/digits, unique per project
              </span>
            </div>
            <div className="field">
              <label>Contract No.</label>
              <input
                className="input"
                placeholder="e.g. HSP-2024-001"
                value={newProjectForm.contract_no}
                onChange={(e) =>
                  setNewProjectForm({
                    ...newProjectForm,
                    contract_no: e.target.value,
                  })
                }
              />
            </div>
            <div className="field">
              <label>Contractor Name</label>
              <input
                className="input"
                placeholder="e.g. ABC Construction Sdn Bhd"
                value={newProjectForm.contractor_name}
                onChange={(e) =>
                  setNewProjectForm({
                    ...newProjectForm,
                    contractor_name: e.target.value,
                  })
                }
              />
            </div>
            <div className="field">
              <label>Description</label>
              <textarea
                className="textarea"
                rows={3}
                placeholder="Brief description of the project…"
                value={newProjectForm.description}
                onChange={(e) =>
                  setNewProjectForm({
                    ...newProjectForm,
                    description: e.target.value,
                  })
                }
              />
            </div>
          </div>
          <div className="modal-foot">
            <button className="btn" onClick={() => setNewProjectModal(false)}>
              Cancel
            </button>
            <button
              className="btn btn-primary"
              onClick={handleCreateProject}
              disabled={
                !newProjectForm.name.trim() ||
                newProjectForm.project_code.trim().length !== 6 ||
                creating
              }
            >
              {creating ? "Creating…" : "Create Project"}
            </button>
          </div>
        </div>
      </div>

      {/* Limit Reached Modal */}
      <div className={`modal-backdrop ${limitReachedModal ? "show" : ""}`}>
        <div className="modal">
          <div className="modal-head">
            <h3>Project Limit Reached</h3>
            <button
              className="btn btn-ghost btn-icon"
              onClick={() => setLimitReachedModal(false)}
            >
              <i className="bi bi-x-lg" />
            </button>
          </div>
          <div className="modal-body">
            <p>
              You have reached the maximum limit of {MAXIMUM_PROJECT_COUNT}{" "}
              {MAXIMUM_PROJECT_COUNT === 1 ? "project" : "projects"}. No more
              projects can be added.
            </p>
          </div>
          <div className="modal-foot">
            <button
              className="btn btn-primary"
              onClick={() => setLimitReachedModal(false)}
            >
              OK
            </button>
          </div>
        </div>
      </div>
    </>
  );
};

export default TopBar;
